import type { InSnipCard } from "../types";

/**
 * Catch me up: a finite, calm selection for readers who avoid the news because of how it
 * makes them feel (the largest underserved segment; the top avoidance reason is mood).
 * Picks a small, capped set from the cards already in memory: bright-side stories first,
 * calm fields next, and heavy stories excluded outright. Deterministic and pure.
 * House style: no em dashes or en dashes anywhere in copy.
 */

export const CALM_COUNT = 5;

/** Fields that read calm by default. */
const CALM_CATEGORIES = new Set([
  "Culture & Curiosity",
  "Science & Systems",
  "Planet & Environment",
  "Sport & Play",
]);

/** A story is heavy when its headline or essence leads with harm. Small, surgical list:
 *  this must never flag ordinary hard news like budgets or elections. */
const HEAVY = /\b(dies|dead|death|killed|killing|murder|war|missile|airstrike|shooting|stabbing|assault|massacre|crash kills|fatal|famine|abuse)\b/i;

function isHeavy(card: InSnipCard): boolean {
  return HEAVY.test(`${card.headline} ${card.essence}`);
}

function freshness(card: InSnipCard): number {
  const t = Date.parse(card.publishedAt || "");
  return Number.isFinite(t) ? t : 0;
}

/** Pick up to `count` calm cards the reader has not seen. Good News first (capped at two,
 *  variety beats saccharine), then calm fields, then anything gentle enough to pass. */
export function pickCalmCards(
  cards: InSnipCard[],
  seenIds: Iterable<string>,
  count = CALM_COUNT,
): InSnipCard[] {
  const seen = new Set(seenIds);
  const pool = cards.filter(
    (c) =>
      c.id &&
      !seen.has(c.id) &&
      !isHeavy(c) &&
      (c.contentType === undefined ||
        c.contentType === "news" ||
        c.contentType === "knowledge" ||
        c.contentType === "explainer"),
  );
  const byFresh = (a: InSnipCard, b: InSnipCard) => freshness(b) - freshness(a);

  const good = pool.filter((c) => c.goodNews).sort(byFresh);
  const calm = pool.filter((c) => !c.goodNews && CALM_CATEGORIES.has(String(c.category))).sort(byFresh);
  const rest = pool.filter((c) => !c.goodNews && !CALM_CATEGORIES.has(String(c.category))).sort(byFresh);

  const out: InSnipCard[] = [];
  const used = new Set<string>();
  const take = (list: InSnipCard[], n: number) => {
    for (const c of list) {
      if (out.length >= count || n <= 0) return;
      if (used.has(c.id)) continue;
      // Keep the set varied: at most two cards per field.
      const cat = String(c.category ?? "");
      const inCat = out.filter((x) => String(x.category ?? "") === cat).length;
      if (cat && inCat >= 2) continue;
      used.add(c.id);
      out.push(c);
      n -= 1;
    }
  };
  take(good, 2);
  take(calm, count - out.length);
  take(rest, count - out.length);
  return out;
}
